import { createAsyncThunk, createSelector, createSlice } from "@reduxjs/toolkit"
import {
  generateSamplesForObservationAndSubmitToUDL,
  getDataSubmissions,
  getSubmissionById,
} from "./api"
import { allowUpdate } from "../../app/store"

const initialState = {
  statuses: {
    draft: {
      name: "draft",
      colorCode: "#ffd8ad",
    },
    "in-progress": {
      name: "in-progress",
      colorCode: "#a8c6fa",
    },
    submitted: {
      name: "submitted",
      colorCode: "#cbe8ba",
    },
    failed: {
      name: "failed",
      colorCode: "#f6a2bf",
    },
  },
  byId: {}, // A map of data submissions by id
  matchesByCtx: {
    // root context powering the data submissions list view
    root: {
      _loadStatus: "idle", // Information about the loading status of a call
      _errorMessage: undefined,
      ids: [], // data submissions by id
      page: {}, // Pagination information
    },
    /*
      Example of other contexts
      "observation/123": {
        ids: [],
        page: {}, // Pagination information
      },
    */
  },
  selectedSamplesIds: [], // a list of samples to be added to a new data submission
  submittedDataSubmissionsByObservationID: {},
}

/**
 * Thunk to perform async logic to fetch the data submissions matching the provided filters
 *
 * @type {AsyncThunk<*, {readonly filter?: *, readonly context?: *, readonly page?: *}, {}>}
 */
export const fetchDataSubmissions = createAsyncThunk(
  "submissions/fetch",
  /**
   *
   * @param filter the filters to apply to the data submissions search
   * @param page Pagination information
   * @param context The context from where the query is being made.  If its from the data submissions list view then root otherwise examples could be `observation/123`
   * @returns {Promise<*>}
   */
  async ({ filter, page, context }) => {
    const response = await getDataSubmissions({
      filter,
      page,
    })
    if (context) {
      response.context = context
    }
    return response
  }
)

/**
 * Thunk to perform async logic to fetch a data submission by its ID
 * @type {AsyncThunk<*, void, {}>}
 */
export const fetchSubmissionById = createAsyncThunk(
  "submission/fetch",
  async (submissionId) => {
    return await getSubmissionById(submissionId)
  }
)

/**
 * Thunk to perform async logic to generate the samples for an observation and submit to the UDL
 *
 * @type {AsyncThunk<*, {readonly filter?: *, readonly context?: *, readonly page?: *}, {}>}
 */
export const generateSamplesAndSendToUDl = createAsyncThunk(
  "submission/generate/udl",
  /**
   *
   * @param observationId
   * @returns {Promise<*>}
   */
  async ({ observationId }) => {
    const response = await generateSamplesForObservationAndSubmitToUDL({
      observationId,
    })

    return response
  }
)

/**
 * Data submissions reducer
 */
export const submissionsSlice = createSlice({
  name: "submissions",
  initialState,
  // The `reducers` field lets us define reducers and generate associated actions
  reducers: {
    clearMatches: (state, action) => {
      state.matchesByCtx[action?.payload || "root"].ids = []
    },
    // Adds a sample to a new data submission
    addSampleToSubmission: (state, action) => {
      state.selectedSamplesIds.push(action.payload)
    },
    removeSampleFromSubmission: (state, action) => {
      state.selectedSamplesIds = state.selectedSamplesIds.filter(
        (s) => s != action.payload
      )
    },
  },
  // The `extraReducers` field lets the slice handle actions defined elsewhere,
  // including actions generated by createAsyncThunk or in other slices.
  extraReducers: (builder) => {
    builder
      .addCase(fetchDataSubmissions.pending, (state, action) => {
        let context = action?.meta?.arg?.context || "root"
        if (!state.matchesByCtx[context]) {
          state.matchesByCtx[context] = {
            ids: [],
            page: {},
          }
        }
        state.matchesByCtx[context]._loadStatus = "loading"
      })
      .addCase(fetchDataSubmissions.fulfilled, (state, action) => {
        let context = action?.meta?.arg?.context || "root"
        if (state.matchesByCtx[context]) {
          state.matchesByCtx[context]._loadStatus = "idle"
          if (allowUpdate(state.matchesByCtx[context].at, action.payload.at)) {
            // Don't reset if we are paging
            if (action?.payload?.page?.start_index === 0) {
              state.matchesByCtx[context].ids = []
            }
            action?.payload?.data?.map((submission) => {
              // Add the submission to state by id (this can include ids not retrieved in this call)
              state.byId[submission.id] = {
                ...submission,
                _at: action.payload.at,
              }
              // add the ids of the submission that match from this query
              if (
                state.matchesByCtx[context].ids.indexOf(submission.id) === -1
              ) {
                state.matchesByCtx[context].ids.push(submission.id)
              }
            })
            state.matchesByCtx[context].page = action.payload.page
            state.matchesByCtx[context].at = action.payload.at
          }
        }
      })
      .addCase(fetchDataSubmissions.rejected, (state, action) => {
        let context = action?.meta?.arg?.context || "root"
        state.matchesByCtx[context]._loadStatus = "rejected"
        state.matchesByCtx[context]._errorMessage = action?.error?.message
      })
      .addCase(fetchSubmissionById.pending, (state, action) => {
        if (!state.byId[action.meta.arg]) {
          state.byId[action.meta.arg] = {}
        }
        state.byId[action.meta.arg]._loadStatus = "loading"
      })
      .addCase(fetchSubmissionById.fulfilled, (state, action) => {
        if (action?.payload?.id) {
          if (
            allowUpdate(
              state.byId[action.payload.id]
                ? state.byId[action.payload.id]._at
                : undefined,
              action.payload._at
            )
          ) {
            state.byId[action.payload.id] = action.payload
            state.byId[action.payload.id]._loadStatus = "idle"
          }
        }
      })
      .addCase(fetchSubmissionById.rejected, (state, action) => {
        if (!state.byId[action.meta.arg]) {
          state.byId[action.meta.arg] = {}
        }
        state.byId[action.meta.arg]._loadStatus = "rejected"
        state.byId[action.meta.arg]._errorMessage += action.payload
      })
      .addCase(generateSamplesAndSendToUDl.pending, (state, action) => {
        let obsId = action?.meta?.arg?.observationId
        state.submittedDataSubmissionsByObservationID[obsId] = {
          _loadStatus: "loading",
        }
      })
      .addCase(generateSamplesAndSendToUDl.fulfilled, (state, action) => {
        state.byId[action?.payload?.id] = action?.payload
        state.submittedDataSubmissionsByObservationID[
          action?.payload?.observation_id
        ] = { ...action?.payload, _loadStatus: "idle" }
      })
      .addCase(generateSamplesAndSendToUDl.rejected, (state, action) => {
        state.submittedDataSubmissionsByObservationID[
          action?.meta?.arg?.observationId
        ] = { _errorMessage: action?.error?.message, _loadStatus: "rejected" }
      })
  },
})

// Selector for all submissions
export const selectSubmissionsById = (state) => state.submissions.byId
// Selector for the statuses that a submission can be in
export const selectSubmissionStatutes = (state) => state.submissions.statuses
// Selector for the samples we have selected to become a new data submission
export const selectSubmissionSelectedSamples = (state) =>
  state.submissions.selectedSamplesIds

export const selectSubmittedDataSubmissionsByObservationID = (state) =>
  state.submissions.submittedDataSubmissionsByObservationID

/**
 * Takes in a context parameter and returns matches from matchesByContext[context]
 * Usage in components: const items = useSelector((state) => selectSubmissionsByContext(state, `rso/${id}`))
 */
export const selectSubmissionsByContext = createSelector(
  [
    // first input - extract value from `state`
    (state) => state.submissions.matchesByCtx,
    // Take the second arg, `context`, and forward to the output selector
    (state, context) => context,
  ],
  // Output selector gets (`submissions, context)` as args
  (matchesByCtx, context) => {
    return matchesByCtx[context]
  }
)

/**
 * Takes in a list of ids as a parameter and returns matches from byIds
 * Usage in components: const items = useSelector((state) => selectSubmissionsByIds(state, ["1", "2"]))
 */
export const selectSubmissionsByIds = createSelector(
  [
    // first input - extract value from `state`
    selectSubmissionsById,
    // Take the second arg, `ids`, and forward to the output selector
    (state, ids) => ids,
  ],
  // Output selector gets (`byId, ids)` as args
  (byId, ids) => {
    return ids.map((id) => byId[id] || { id })
  }
)

export const {
  clearMatches,
  addSampleToSubmission,
  removeSampleFromSubmission,
} = submissionsSlice.actions

export default submissionsSlice.reducer
